import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LayoutTemplate, Plus, Presentation as PresentationIcon, Users } from 'lucide-react'

import ScreenShell from 'components/ScreenShell/ScreenShell'
import routes from 'constants/routes'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs'
import { toast } from 'components/ui/toast-store'
import {
	createPresentation,
	createPresentationFromTemplate,
	deletePresentation,
	duplicatePresentation,
	fetchPresentations,
	updatePresentation,
} from 'slices/presentationSlice'

import { presentationEditPath, presentationPresentPath } from '../featureFlag'
import type { AssetOption, DeckDefinition, PresentationSummary } from '../types/presentation.types'
import { makeSlide, makeStory, newId } from '../utils/deckOps'
import { announceTemplateNotices, droppedFromDeck } from '../utils/templateNotices'
import { ManageTemplatesSheet } from './ManageTemplatesSheet'
import { NewPresentationDialog, type FromTemplateValues, type PresentationDialogMode } from './NewPresentationDialog'
import { PartnerSharingDialog } from './PartnerSharingDialog'
import { PresentationCard } from './PresentationCard'

type GalleryTab = 'mine' | 'shared'

/**
 * A new deck's first story, about the chosen asset: one title slide named for
 * it, so the editor opens on something rather than an empty sorter.
 */
export const firstStoryFor = (asset: AssetOption): DeckDefinition => {
	const story = makeStory(newId(), { title: asset.name, anchor: { scope: asset.scope, entityId: asset.entityId } })
	return {
		theme: 'default',
		transition: 'flip',
		stories: [{ ...story, slides: [{ ...makeSlide(newId(), 'title'), title: asset.name }] }],
	}
}

/**
 * The Presentations gallery (MYD-494): the user's decks, and decks their
 * partners have shared with them.
 *
 * Desktop-only, like Explorer. The present route is not, because people present
 * from an iPad; a phone reaching a deck gets there through a link, not here.
 */
export function PresentationsScreen() {
	const dispatch = useDispatch<any>()
	const navigate = useNavigate()
	const { list, listLoaded, isLoading, isSaving } = useSelector((state: any) => state.presentations)

	// `?tab=shared` opens on decks shared with me: where the "Pat shared 3
	// presentations with you" notification points (MYD-549).
	const [searchParams] = useSearchParams()
	const [tab, setTab] = useState<GalleryTab>(searchParams.get('tab') === 'shared' ? 'shared' : 'mine')
	const [dialog, setDialog] = useState<{ mode: PresentationDialogMode; target?: PresentationSummary } | null>(null)
	const [dialogError, setDialogError] = useState<string | null>(null)
	// "Share with partners..." from a card's menu (MYD-526).
	const [sharingDeck, setSharingDeck] = useState<PresentationSummary | null>(null)
	// The user's own templates and the starters, of both kinds (MYD-604).
	const [managingTemplates, setManagingTemplates] = useState(false)

	useEffect(() => {
		dispatch(fetchPresentations())
	}, [dispatch])

	const mine = useMemo(() => (list as PresentationSummary[]).filter(deck => deck.isOwner), [list])
	const shared = useMemo(() => (list as PresentationSummary[]).filter(deck => !deck.isOwner), [list])
	const visible = tab === 'mine' ? mine : shared

	const openDialog = (mode: PresentationDialogMode, target?: PresentationSummary) => {
		setDialogError(null)
		setDialog({ mode, target })
	}

	const submitDialog = async ({ name, asset }: { name: string; asset: AssetOption | null }) => {
		if (!dialog) return
		setDialogError(null)

		if (dialog.mode === 'rename' && dialog.target) {
			const result = await dispatch(updatePresentation({ id: dialog.target.id, input: { name } }))
			if (updatePresentation.rejected.match(result)) return setDialogError(result.payload as string)
			setDialog(null)
			toast.success('Presentation renamed')
			return
		}

		const result = await dispatch(createPresentation({ name, ...(asset ? { definition: firstStoryFor(asset) } : {}) }))
		if (createPresentation.rejected.match(result)) return setDialogError(result.payload as string)
		setDialog(null)
		navigate(presentationEditPath(result.payload.id))
	}

	// A deck built from a deck template (MYD-604): the server binds every
	// story, so the editor opens on a finished draft.
	const createFromTemplate = async ({ templateKey, asset, name }: FromTemplateValues) => {
		setDialogError(null)
		const result = await dispatch(
			createPresentationFromTemplate({ templateKey, anchor: { scope: asset.scope, entityId: asset.entityId }, ...(name ? { name } : {}) }),
		)
		if (createPresentationFromTemplate.rejected.match(result)) return setDialogError(result.payload as string)
		setDialog(null)
		announceTemplateNotices(result.payload, droppedFromDeck)
		navigate(presentationEditPath(result.payload.presentation.id))
	}

	const duplicate = async (deck: PresentationSummary) => {
		const result = await dispatch(duplicatePresentation(deck.id))
		if (duplicatePresentation.rejected.match(result)) return toast.error(result.payload as string)
		// The copy is always the caller's own, so show it where it landed.
		setTab('mine')
		toast.success(`"${result.payload.name}" added to your presentations`)
	}

	const remove = async (deck: PresentationSummary) => {
		const confirmed = await toast.confirm({
			title: 'Delete this presentation?',
			message: deck.visibility === 'shared'
				? `"${deck.name}" will be deleted, and your partners will no longer see it.`
				: `"${deck.name}" will be deleted.`,
			confirmLabel: 'Delete',
			variant: 'destructive',
		})
		if (!confirmed) return

		const result = await dispatch(deletePresentation(deck.id))
		if (deletePresentation.rejected.match(result)) return toast.error(result.payload as string)
		toast.success('Presentation deleted')
	}

	return (
		<ScreenShell>
			<main className="mx-auto w-full max-w-[1400px] p-6">
				<div className="mb-6 flex items-start justify-between gap-4">
					<div>
						<h1 className="text-2xl font-bold text-gray-900">Presentations</h1>
						<p className="text-sm text-gray-500">Tell the story of your assets with live data, and present it right here.</p>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="ghost" isOutline onClick={() => setManagingTemplates(true)}>
							<LayoutTemplate className="w-4 h-4 mr-1.5" />
							Templates
						</Button>
						<Button onClick={() => openDialog('create')}>
							<Plus className="w-4 h-4 mr-1.5" />
							New presentation
						</Button>
					</div>
				</div>

				<Tabs value={tab} onValueChange={value => setTab(value as GalleryTab)} className="mb-5">
					<TabsList>
						<TabsTrigger value="mine">Mine ({mine.length})</TabsTrigger>
						<TabsTrigger value="shared">Shared with me ({shared.length})</TabsTrigger>
					</TabsList>
				</Tabs>

				{!listLoaded && isLoading ? (
					<Spinner type="circular" containerHeight="240px" />
				) : visible.length === 0 ? (
					tab === 'mine' ? (
						<EmptyState
							icon={PresentationIcon}
							title="No presentations yet"
							description="Build a deck from your locations, projects, portfolios and evaluations, then present it full-screen."
							action={{ label: 'New presentation', onClick: () => openDialog('create') }}
						/>
					) : (
						<EmptyState
							icon={Users}
							title="Nothing shared with you"
							description="When a partner shares a presentation, it appears here. You will see it with your own access to each asset."
						/>
					)
				) : (
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{visible.map(deck => (
							<PresentationCard
								key={deck.id}
								presentation={deck}
								onPresent={() => navigate(presentationPresentPath(deck.id), { state: { from: routes.PRESENTATIONS } })}
								onEdit={() => navigate(presentationEditPath(deck.id))}
								onDuplicate={() => duplicate(deck)}
								onRename={() => openDialog('rename', deck)}
								onDelete={() => remove(deck)}
								onShare={deck.isOwner ? () => setSharingDeck(deck) : undefined}
							/>
						))}
					</div>
				)}
			</main>

			{sharingDeck && (
				<PartnerSharingDialog
					open
					presentationId={sharingDeck.id}
					presentationName={sharingDeck.name}
					onClose={() => setSharingDeck(null)}
					// Refetch, so the card's badge says who it is shared with now.
					onSaved={() => dispatch(fetchPresentations())}
				/>
			)}

			<NewPresentationDialog
				open={dialog !== null}
				mode={dialog?.mode ?? 'create'}
				initialName={dialog?.target?.name ?? ''}
				isSaving={isSaving}
				error={dialogError}
				onClose={() => setDialog(null)}
				onSubmit={submitDialog}
				onCreateFromTemplate={createFromTemplate}
			/>

			<ManageTemplatesSheet open={managingTemplates} onClose={() => setManagingTemplates(false)} />
		</ScreenShell>
	)
}

interface EmptyStateProps {
	icon: typeof PresentationIcon
	title: string
	description: string
	action?: { label: string; onClick: () => void }
}

/** The ui-design skill's empty state. */
function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
	return (
		<div className="flex flex-col items-center justify-center py-12 text-center">
			<div className="mb-4 rounded-full bg-gray-100 p-3">
				<Icon className="w-6 h-6 text-gray-400" />
			</div>
			<h3 className="mb-1 text-lg font-medium text-gray-900">{title}</h3>
			<p className="mb-4 max-w-sm text-sm text-gray-500">{description}</p>
			{action && (
				<Button onClick={action.onClick}>
					<Plus className="w-4 h-4 mr-1.5" />
					{action.label}
				</Button>
			)}
		</div>
	)
}

export default PresentationsScreen
